import { CheckIcon, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useSetCurrentOrgId } from '@/features/org/hooks/use-current-org-id';
import { useCurrentOrg } from '@/features/org/hooks/use-org';
import { useUserOrgs } from '@/features/org/hooks/use-user-orgs';
import { OrgId } from '@/features/org/types/org-id';
import { defaultOrgName } from '@/features/org/utils/default-org-name';
import AppLogo from '@/shared/assets/app_logo.svg?react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown';
import { Org } from '@/shared/types/api';
import { getInitials } from '@/shared/utils/avatar-util';
import { cn } from '@/shared/utils/cn';

export const OrganisationButton = () => {
  const [open, setOpen] = useState(false);
  const { data: orgs } = useUserOrgs();
  const { data: currentOrg } = useCurrentOrg();

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Avatar className="size-11 cursor-pointer rounded-lg">
          <AvatarImage src={currentOrg?.image_url || ''} alt="Organisation" />
          <AvatarFallback className="rounded-lg">
            {currentOrg ? (
              <span className="text-xs">
                {getInitials(currentOrg.name ?? defaultOrgName)}
              </span>
            ) : (
              <AppLogo className="size-11" aria-hidden="true" />
            )}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="max-h-[50vh] w-64 min-w-64 overflow-y-auto rounded-[10px] p-0"
        side={'right'}
        align="start"
        sideOffset={20}
      >
        {orgs?.map((org) => (
          <OrgItem
            key={org.id}
            org={org}
            isSelected={currentOrg?.id === org.id}
            onSelect={() => setOpen(false)}
          />
        ))}
        <AddOrgItem onSelect={() => setOpen(false)} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface OrgItemProps {
  org: Org;
  isSelected: boolean;
  onSelect: () => void;
}

const OrgItem = ({ org, isSelected, onSelect }: OrgItemProps) => {
  const setCurrentOrgId = useSetCurrentOrgId();
  const navigate = useNavigate();

  return (
    <button
      type="button"
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left transition-colors',
        isSelected ? 'bg-white/15 hover:bg-white/20' : 'hover:bg-white/10',
      )}
      onClick={() => {
        setCurrentOrgId(org.id as OrgId);
        if (!isSelected) {
          navigate(paths.dashboard.getHref());
        }
        onSelect();
      }}
    >
      <Avatar className="size-9 shrink-0">
        <AvatarImage
          src={org.image_url || ''}
          alt={org.name ?? defaultOrgName}
        />
        <AvatarFallback className="text-xs">
          {getInitials(org.name ?? defaultOrgName)}
        </AvatarFallback>
      </Avatar>
      <span className="flex-1 truncate text-sm text-white">
        {org.name ?? defaultOrgName}
      </span>
      {isSelected && <CheckIcon className="size-4 shrink-0 text-white" />}
    </button>
  );
};

const AddOrgItem = ({ onSelect }: { onSelect: () => void }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  return (
    <button
      type="button"
      className="flex w-full cursor-pointer items-center gap-3 border-t border-white/10 px-3 py-2 text-left transition-colors hover:bg-white/10"
      onClick={() => {
        navigate(paths.org.create.getHref());
        onSelect();
      }}
    >
      <Avatar className="size-9 shrink-0 bg-white/10">
        <AvatarFallback className="bg-transparent">
          <Plus className="size-4 text-white" />
        </AvatarFallback>
      </Avatar>
      <span className="line-clamp-2 flex-1 text-sm text-white">
        {t('navbar.org_picker.add_org')}
      </span>
    </button>
  );
};
